import { useState } from 'react'
import { Flag, AlertTriangle, Star, Loader2, ChevronLeft, ChevronRight, Check, X } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useReviews } from '@/lib/data'

interface ReviewsViewProps {
  client: SupabaseClient | null
}

export function ReviewsView({ client }: ReviewsViewProps) {
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<'all' | 'flagged' | 'reported'>('all')
  const limit = 20

  const { reviews, total, stats, isLoading, error, flagReview } = useReviews(client, {
    page,
    limit,
    status,
  })

  const totalPages = Math.ceil(total / limit)

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-[var(--text-muted)]'}
          />
        ))}
      </div>
    )
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <Star size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view reviews</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-[var(--text-main)]">Reviews & Moderation</h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => { setStatus('all'); setPage(1) }}
          className={`bg-[var(--bg-card)] border p-4 rounded-xl text-left transition-colors ${
            status === 'all' ? 'border-[var(--primary)]' : 'border-[var(--border)] hover:border-[var(--text-muted)]'
          }`}
        >
          <p className="text-[var(--text-muted)] text-sm">Total Reviews</p>
          <p className="text-2xl font-bold text-[var(--text-main)] mt-1">
            {isLoading ? '...' : stats.total.toLocaleString()}
          </p>
        </button>
        <button
          onClick={() => { setStatus('flagged'); setPage(1) }}
          className={`bg-[var(--bg-card)] border p-4 rounded-xl text-left transition-colors ${
            status === 'flagged' ? 'border-[var(--primary)]' : 'border-[var(--border)] hover:border-[var(--text-muted)]'
          }`}
        >
          <p className="text-[var(--text-muted)] text-sm flex items-center gap-2">
            <Flag size={14} className="text-amber-400" />
            Flagged
          </p>
          <p className="text-2xl font-bold text-amber-400 mt-1">
            {isLoading ? '...' : stats.flagged}
          </p>
        </button>
        <button
          onClick={() => { setStatus('reported'); setPage(1) }}
          className={`bg-[var(--bg-card)] border p-4 rounded-xl text-left transition-colors ${
            status === 'reported' ? 'border-[var(--primary)]' : 'border-[var(--border)] hover:border-[var(--text-muted)]'
          }`}
        >
          <p className="text-[var(--text-muted)] text-sm flex items-center gap-2">
            <AlertTriangle size={14} className="text-red-400" />
            Reported
          </p>
          <p className="text-2xl font-bold text-red-400 mt-1">
            {isLoading ? '...' : stats.reported}
          </p>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Reviews List */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            No reviews found
          </div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {reviews.map((review) => (
              <div key={review.id} className="p-4 hover:bg-[var(--bg-element)]">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {renderStars(review.rating)}
                      <span className="text-[var(--text-muted)] text-sm">
                        {formatDate(review.created_at)}
                      </span>
                      {review.is_flagged && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 text-amber-400 text-xs rounded-full">
                          <Flag size={10} />
                          Flagged
                        </span>
                      )}
                      {review.report_count > 0 && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/10 text-red-400 text-xs rounded-full">
                          <AlertTriangle size={10} />
                          {review.report_count} reports
                        </span>
                      )}
                    </div>
                    <p className="text-[var(--text-main)]">
                      {review.review_text || <span className="text-[var(--text-muted)] italic">No review text</span>}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-[var(--text-muted)]">
                      <span>Product: {review.product_code}</span>
                      <span>User: {review.user_id.slice(0, 8)}...</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {review.is_flagged ? (
                      <button
                        onClick={() => flagReview(review.id, false)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 text-sm"
                      >
                        <Check size={14} />
                        Unflag
                      </button>
                    ) : (
                      <button
                        onClick={() => flagReview(review.id, true)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 text-sm"
                      >
                        <Flag size={14} />
                        Flag
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[var(--text-muted)] text-sm">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} reviews
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronLeft size={16} className="text-[var(--text-muted)]" />
            </button>
            <span className="text-[var(--text-main)] px-4">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronRight size={16} className="text-[var(--text-muted)]" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
